import { forwardRef } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { trackCta } from "@/lib/analytics/cta";

export interface TrackedLinkProps extends LinkProps {
  /** Stable slug for what this CTA does (unchanged when copy changes). */
  ctaId: string;
  /** Where on the site the CTA was clicked (e.g. `header`, `footer`). */
  ctaLocation: string;
}

/**
 * A react-router `<Link>` that fires a `cta_click` event before navigating.
 * Drop-in replacement for `<Link>` — all other props (className, children,
 * onClick, etc.) pass straight through, so styling is untouched. The tracking
 * fires first, then any caller-supplied `onClick` runs, then navigation
 * proceeds as normal.
 */
export const TrackedLink = forwardRef<HTMLAnchorElement, TrackedLinkProps>(
  ({ ctaId, ctaLocation, to, onClick, ...rest }, ref) => {
    const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
      trackCta({
        cta_id: ctaId,
        location: ctaLocation,
        destination: typeof to === "string" ? to : null,
      });
      onClick?.(event);
    };
    return <Link ref={ref} to={to} onClick={handleClick} {...rest} />;
  },
);
TrackedLink.displayName = "TrackedLink";
